import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Truck } from "lucide-react";
import { openDdt } from "@/components/warehouse/MovementsTab";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Empty } from "@/components/Common";
import { RunNow } from "@/components/warehouse/PurchaseOrders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const NONE = "none";

const initQty = (rf) => {
  const q = {};
  rf.lines.forEach((l) => {
    if (!l.serialized) { q[l.item_id] = l.available == null ? l.qty : Math.min(l.qty, Math.max(l.available, 0)); return; }
    const split = l.split || {};
    Object.entries(split).forEach(([c, n]) => { q[`${l.item_id}|${c}`] = n; });
    const rest = l.qty - Object.values(split).reduce((s, n) => s + n, 0);
    if (rest > 0) q[`${l.item_id}|${NONE}`] = Math.min(rest, l.available_by_customer?.[NONE] || 0);
  });
  return q;
};

const SerialSplit = ({ l, rf, qty, setQty, disabled }) => {
  const { nameOf } = useData();
  const avail = l.available_by_customer || {};
  const keys = [...new Set([...Object.keys(avail), ...Object.keys(l.split || {})])].sort((a, b) => (a === NONE) - (b === NONE));
  if (!keys.length) return <p className="pl-4 text-xs text-red-700">Nessun seriale disponibile nel centrale</p>;
  return (
    <div className="space-y-1 border-l-2 pl-4" data-testid={`refill-split-${rf.number}-${l.code}`}>
      {keys.map((c) => (
        <div key={c} className="grid grid-cols-[1fr_100px_160px] items-center gap-2 text-xs">
          <span>{c === NONE ? "Senza committente" : nameOf("parties", c)}{l.split?.[c] ? <span className="ml-1 font-semibold text-amber-700">· serve per ordini pianificati</span> : null}</span>
          <Input type="number" min="0" max={avail[c] || 0} className="h-7" value={qty[`${l.item_id}|${c}`] ?? 0} disabled={disabled}
            onChange={(e) => setQty({ ...qty, [`${l.item_id}|${c}`]: Math.max(0, Math.min(Number(e.target.value), avail[c] || 0)) })} data-testid={`refill-qty-${rf.number}-${l.code}-${c}`} />
          <span className="text-right font-mono text-muted-foreground">disp. {avail[c] || 0}</span>
        </div>
      ))}
    </div>
  );
};

const RefillCard = ({ rf, onDone }) => {
  const { can } = useAuth();
  const [qty, setQty] = useState({});
  useEffect(() => { setQty(initQty(rf)); }, [rf]);
  const payload = () => rf.lines.flatMap((l) => (l.serialized
    ? Object.entries(qty).filter(([k, n]) => k.startsWith(`${l.item_id}|`) && Number(n) > 0).map(([k, n]) => { const c = k.split("|")[1]; return { item_id: l.item_id, qty: Number(n), customer_id: c === NONE ? null : c }; })
    : [{ item_id: l.item_id, qty: Number(qty[l.item_id]) || 0 }]));
  const execute = async (withDdt) => {
    try {
      const r = await api.post(`/warehouse/van-refills/${rf.id}/execute`, { lines: payload() });
      const n = new Set(payload().map((x) => x.item_id)).size;
      toast.success(rf.source === "TECH" ? `Richiesta ${rf.number} evasa (${n} ${n === 1 ? "articolo" : "articoli"})` : `Reintegro eseguito (${n} ${n === 1 ? "articolo" : "articoli"})`);
      r.data.warnings.forEach((w) => toast.warning(w));
      if (withDdt) {
        try {
          const d = await api.post("/warehouse/ddt", { movement_ids: r.data.movement_ids, notes: rf.source === "TECH" ? `Richiesta ${rf.number} di ${rf.requested_by}` : `Reintegro ${rf.number}` });
          toast.success(`DDT n. ${d.data.number} emesso`);
          await openDdt(d.data.id);
        } catch (e) {
          toast.error(`DDT non emesso: ${errMsg(e)}`);
        }
      }
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reject = async () => {
    const note = window.prompt("Motivo del rifiuto (verrà mostrato al tecnico)", "");
    if (note === null) return;
    try {
      await api.post(`/warehouse/van-refills/${rf.id}/reject`, { note });
      toast.success("Richiesta rifiutata");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const tech = rf.source === "TECH";
  const [pickup, setPickup] = useState(rf.pickup_at || "");
  const ready = async () => {
    try {
      await api.post(`/warehouse/van-refills/${rf.id}/ready`, { pickup_at: pickup });
      toast.success("Tecnico avvisato del ritiro");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className={`border bg-white p-4 ${tech ? "border-l-4 border-l-amber-500" : ""}`} data-testid={`refill-card-${rf.number}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Truck className="h-4 w-4" />
        <span className="font-semibold">{rf.to_name}</span>
        <span className="font-mono text-xs text-muted-foreground">{rf.number}</span>
        {tech && <span className="bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid={`refill-tech-badge-${rf.number}`}>Richiesta di {rf.requested_by}</span>}
        <span className="ml-auto text-xs text-muted-foreground">da {rf.from_name || "nessun magazzino centrale"}</span>
      </div>
      {tech && rf.notes && <p className="mb-3 border-l-2 pl-2 text-sm italic text-muted-foreground" data-testid={`refill-notes-${rf.number}`}>{rf.notes}</p>}
      <div className="space-y-1.5">
        {rf.lines.map((l) => (
          <div key={l.item_id} className="space-y-1">
          <div className="grid grid-cols-[1fr_100px_160px] items-center gap-2 text-sm">
            <span><span className="font-mono text-xs font-semibold">{l.code}</span> {l.name}{l.serialized && <span className="ml-1 text-xs text-muted-foreground">· servono {l.qty}</span>}</span>
            {l.serialized ? <span className="text-right font-mono text-sm" data-testid={`refill-total-${rf.number}-${l.code}`}>{Object.entries(qty).filter(([k]) => k.startsWith(`${l.item_id}|`)).reduce((s, [, n]) => s + Number(n || 0), 0)} pz</span> : (
            <Input type="number" min="0" step="any" max={Math.max(l.available ?? 0, 0)} className="h-8" value={qty[l.item_id] ?? 0}
              onChange={(e) => setQty({ ...qty, [l.item_id]: Math.max(0, l.available != null ? Math.min(Number(e.target.value), Math.max(l.available, 0)) : Number(e.target.value)) })}
              disabled={!can("inventory:write")} data-testid={`refill-qty-${rf.number}-${l.code}`} />)}
            <span className={`text-right font-mono text-xs ${l.available < l.qty ? "text-red-700" : "text-muted-foreground"}`}>disp. centrale {l.available ?? "—"} {l.unit}</span>
          </div>
          {l.serialized && <SerialSplit l={l} rf={rf} qty={qty} setQty={setQty} disabled={!can("inventory:write")} />}
          </div>
        ))}
      </div>
      {tech && can("inventory:write") && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3" data-testid={`refill-pickup-${rf.number}`}>
          <span className="text-xs font-semibold">Ritiro</span>
          <Input type="datetime-local" className="h-8 w-52" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid={`refill-pickup-input-${rf.number}`} />
          <Button size="sm" variant="outline" disabled={!pickup} onClick={ready} data-testid={`refill-ready-${rf.number}`}>Pronto: avvisa il tecnico</Button>
          {rf.pickup_at && <span className="text-xs text-emerald-700" data-testid={`refill-pickup-set-${rf.number}`}>Avvisato · ritiro {rf.pickup_at.replace("T", " ")}</span>}
        </div>
      )}
      {can("inventory:write") && (
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => execute(false)} disabled={!rf.from_warehouse_id} data-testid={`refill-execute-${rf.number}`}>{tech ? "Evadi e carica nel furgone" : "Esegui trasferimento al furgone"}</Button>
          <Button size="sm" onClick={() => execute(true)} disabled={!rf.from_warehouse_id} data-testid={`refill-execute-ddt-${rf.number}`}><FileText className="mr-1 h-4 w-4" />{tech ? "Evadi e stampa DDT" : "Esegui e stampa DDT"}</Button>
          {tech && <Button size="sm" variant="outline" onClick={reject} data-testid={`refill-reject-${rf.number}`}>Rifiuta</Button>}
        </div>
      )}
    </div>
  );
};

const NeedsBox = ({ refreshKey }) => {
  const { inCompany } = useData();
  const [d, setD] = useState({ days: [], rows: [] });
  useEffect(() => { api.get("/warehouse/device-needs").then((r) => setD(r.data)).catch(() => {}); }, [refreshKey]);
  const rows = d.rows.filter((r) => inCompany(r.company_id));
  if (!rows.length) return null;
  const short = rows.filter((r) => r.missing);
  return (
    <div className={`mb-4 border bg-white ${short.length ? "border-l-4 border-l-red-600" : ""}`} data-testid="device-needs">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <span className="micro-label">Fabbisogno apparati per committente</span>
        <span className="text-xs text-muted-foreground">attivazioni pianificate {d.days.map((x) => `${x.slice(8, 10)}/${x.slice(5, 7)}`).join(", ")}</span>
        {short.length > 0 && <span className="ml-auto text-xs font-semibold text-red-700" data-testid="device-needs-short">{short.length} {short.length === 1 ? "furgone con apparati insufficienti" : "furgoni con apparati insufficienti"}</span>}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase text-muted-foreground"><th className="px-4 py-1">Furgone</th><th>Committente</th><th className="text-right">Ordini</th><th className="text-right">A bordo</th><th className="text-right">Mancano</th><th className="px-4 text-right">Nel centrale</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={`${r.van_id}${r.customer_id}`} className={`border-t ${r.missing ? "bg-red-50/60" : ""}`} data-testid={`device-need-${r.van_id}-${r.customer_id}`}>
            <td className="px-4 py-1.5">{r.van_name.replace(/^Furgone /, "")}</td><td>{r.customer_name}</td>
            <td className="text-right font-mono">{r.orders}</td><td className="text-right font-mono">{r.devices}</td>
            <td className={`text-right font-mono font-semibold ${r.missing ? "text-red-700" : "text-emerald-700"}`}>{r.missing || "OK"}</td>
            <td className="px-4 text-right font-mono">{r.central_available}</td>
          </tr>
        ))}</tbody>
      </table>
      <p className="border-t px-4 py-1.5 text-[11px] text-muted-foreground">Contano solo gli apparati assegnati alla committente. Per rifornire usa il reintegro qui sotto o Movimenti › Trasferimento scegliendo la committente.</p>
    </div>
  );
};

export const VanRefillsTab = ({ onChanged }) => {
  const { inCompany } = useData();
  const [raw, setRows] = useState([]);
  const [tick, setTick] = useState(0);
  const rows = raw.filter((r) => inCompany(r.company_id)).sort((a, b) => (b.source === "TECH") - (a.source === "TECH"));
  const load = useCallback(() => api.get("/warehouse/van-refills").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); setTick((t) => t + 1); onChanged(); };
  return (
    <div data-testid="van-refills-tab">
      <NeedsBox refreshKey={tick} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">Proposte calcolate sulla dotazione standard per furgone (campo dell'articolo). I seriali vengono prelevati dal magazzino centrale tra quelli della committente scelta, dai più vecchi.</p>
        <div className="ml-auto"><RunNow onDone={changed} /></div>
      </div>
      {rows.length === 0 ? <Empty text="Tutti i furgoni sono in dotazione standard" testId="refills-empty" /> : <div className="grid gap-4 xl:grid-cols-2">{rows.map((rf) => <RefillCard key={rf.id} rf={rf} onDone={changed} />)}</div>}
    </div>
  );
};
